'use client'

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react'

import { User as UserType } from '@dc-shared-web/hooks/api/user'
import { Team } from '@dc-shared-web/hooks/api/team'

import { setupAutoLogoutTime } from '@dc-shared-web/lib/autoLogoutTimer'
import { TESTING_LOCAL_ENDPOINT } from '@dc-shared-web/lib/constants'
import { useAPIToken } from '@dc-shared-web/providers/APITokenProvider'

type User = {
  clearUser: () => void
  user: UserType
  isAuthenticating: boolean
}

export const INITIAL_USER_ID = -1
const initialState: User = {
  clearUser: () => {},
  user: {
    id: INITIAL_USER_ID,
    uuid: '',
    name: '',
    email: '',
    teams: [],
    roles: [],
    cp_csrf_token: '',
    permissions: [],
    should_show_disclaimer: false,
    _currOfficeUUID: '',
    _currOfficeId: 0,
    _currOfficeName: '',
    _isUnaffiliatedUser: false,
    is_super_admin: false,
    created_at: '',
    chameleon_hash: '',
  },
  isAuthenticating: true,
}

export const AuthContext = createContext(initialState)
export const useAuth = () => useContext(AuthContext)

export const getCurrentTeam = (user: UserType) => {
  return user.teams.find((team: Team) => team.is_current)
}

export const AuthProvider = ({
  children,
  publicURL,
  logoutURL,
}: {
  children: React.ReactNode
  publicURL: string | undefined
  logoutURL?: string
}) => {
  const { token } = useAPIToken()

  const [user, setUser] = useState<UserType>(initialState.user)
  const [isAuthenticating, setIsAuthenticating] = useState(true)

  const [userData, setUserData] = useState<UserType | null>(null)
  const [isLoadingUser, setIsLoadingUser] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    // ** TIMER FOR AUTO LOGOUT ** //
    const setupAutoLogout = () => {
      setupAutoLogoutTime(logoutURL)
    }

    setupAutoLogout()
  }, [logoutURL])

  const clearUser = () => {
    setUser(initialState.user)
  }

  const fetchUserData = useCallback(async () => {
    if (!token) return

    setIsLoadingUser(true)
    setError(null)

    try {
      const response = await fetch(
        `${publicURL}/api/secure/user?getDisclaimer=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error('Failed to fetch user data')
      }

      const data = await response.json()
      setUserData(data.data)
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error(String(err)))
    } finally {
      setIsLoadingUser(false)
    }
  }, [token, setUserData, setIsLoadingUser, setError, publicURL])

  useEffect(() => {
    if (token) {
      fetchUserData()
    }
  }, [token, fetchUserData])

  useEffect(() => {
    if (userData) {
      // Set current office info - logic should eventually be moved to the backend
      const currentTeam = getCurrentTeam(userData)
      if (currentTeam) {
        userData._currOfficeUUID = currentTeam.id
        userData._currOfficeId = currentTeam.teamId
        userData._currOfficeName = currentTeam.name
      }

      setUser({
        ...userData,
        _isUnaffiliatedUser:
          !userData.is_super_admin && userData?.teams?.length === 0,
      })
      setIsAuthenticating(false)
    } else if ((!userData && !isLoadingUser) || error) {
      setUser(initialState.user)
      setIsAuthenticating(false)

      if (!TESTING_LOCAL_ENDPOINT) {
        window.location.href = `${publicURL}/logout`
      }
    }
  }, [userData, error, isLoadingUser, publicURL, token])

  return (
    <AuthContext.Provider value={{ user, clearUser, isAuthenticating }}>
      {children}
    </AuthContext.Provider>
  )
}
